/**
 * SurveyPlots — three side-by-side (1/3 width each) views of a survey run's
 * computed trajectory: NE plan view (SVG), vertical section (SVG), and an
 * INTERACTIVE 3D trajectory (three.js). The two SVG plots are plain SVG scaled
 * from the min-curvature points, styled with the app's semantic tokens
 * (--ink / --dim / --line / --surface / --oxbo-p). The 3D view is a lazily-
 * loaded three.js scene (see Trajectory3D) that reuses the SAME min-curvature
 * points. Depths/offsets are metres throughout.
 */
import { lazy, Suspense } from "react";
import { useLocale } from "@hooks/useLocale";
import { fmtDepth } from "@lib/format";
import { computeMinCurvature, computeBounds, type ComputedStation } from "./minCurvature";
import type { SurveyStationOut } from "@api/types";

// Lazy-load the 3D scene: three.js is heavy (~600KB) and only some runs are
// worth spinning up a WebGL context for, so keep it out of the initial bundle.
const Trajectory3D = lazy(() =>
  import("./Trajectory3D").then((m) => ({ default: m.Trajectory3D })),
);

const W = 260; // intrinsic viewBox width per plot
const H = 240; // intrinsic viewBox height per plot
const PAD = 34; // inner padding for axes/labels

/** Map a data range onto a pixel range, guarding a zero-width domain. */
function scaler(dMin: number, dMax: number, pMin: number, pMax: number) {
  const span = dMax - dMin;
  if (!Number.isFinite(span) || Math.abs(span) < 1e-9) {
    const mid = (pMin + pMax) / 2;
    return () => mid;
  }
  return (v: number) => pMin + ((v - dMin) / span) * (pMax - pMin);
}

interface PlotFrameProps {
  title: string;
  children: React.ReactNode;
}

function PlotFrame({ title, children }: PlotFrameProps) {
  return (
    <div style={{ flex: "1 1 0", minWidth: 0 }}>
      <div className="subtitle" style={{ margin: "0 0 6px", fontWeight: 600, color: "var(--dim)" }}>
        {title}
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={title}
        style={{
          width: "100%",
          height: "auto",
          background: "var(--surface)",
          border: "1px solid var(--line)",
          borderRadius: 8,
          display: "block",
        }}
      >
        {children}
      </svg>
    </div>
  );
}

/** The wellbore polyline + start/end markers shared by every view. */
function Path({ pts, id }: { pts: { x: number; y: number }[]; id: string }) {
  if (pts.length === 0) return null;
  const d = pts
    .map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
    .join(" ");
  const start = pts[0];
  const end = pts[pts.length - 1];
  if (start === undefined || end === undefined) return null;
  return (
    <g>
      <path
        d={d}
        fill="none"
        stroke="var(--oxbo-p, var(--brand))"
        strokeWidth={1.75}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {pts.map((p, i) => (
        <circle key={`${id}-${i}`} cx={p.x} cy={p.y} r={1.4} fill="var(--dim)" />
      ))}
      <circle cx={start.x} cy={start.y} r={3} fill="var(--good, #3fb950)" />
      <circle cx={end.x} cy={end.y} r={3} fill="var(--crit, #f85149)" />
    </g>
  );
}

/** A dim axis-label. */
function Label(props: {
  x: number;
  y: number;
  children: React.ReactNode;
  anchor?: "start" | "middle" | "end";
  rotate?: number;
}) {
  const { x, y, children, anchor = "middle", rotate } = props;
  return (
    <text
      x={x}
      y={y}
      fontSize={9}
      fill="var(--dim)"
      textAnchor={anchor}
      transform={rotate ? `rotate(${rotate} ${x} ${y})` : undefined}
    >
      {children}
    </text>
  );
}

/** NE plan view (North vs East) with equal aspect. North is up, East is right. */
function PlanView({ pts }: { pts: ComputedStation[] }) {
  const { t, locale } = useLocale();
  const b = computeBounds(pts)!;
  // Equal aspect: use a single span centred on each axis' midpoint.
  const midE = (b.minE + b.maxE) / 2;
  const midN = (b.minN + b.maxN) / 2;
  const span = Math.max(b.maxE - b.minE, b.maxN - b.minN, 1) * 1.1;
  const sx = scaler(midE - span / 2, midE + span / 2, PAD, W - PAD);
  // North up -> invert the y range.
  const sy = scaler(midN - span / 2, midN + span / 2, H - PAD, PAD);
  const scr = pts.map((p) => ({ x: sx(p.east), y: sy(p.north) }));
  return (
    <PlotFrame title={t("surveys.plots.plan")}>
      <line x1={PAD} y1={H - PAD} x2={W - PAD} y2={H - PAD} stroke="var(--line)" />
      <line x1={PAD} y1={PAD} x2={PAD} y2={H - PAD} stroke="var(--line)" />
      <Path pts={scr} id="plan" />
      <Label x={W / 2} y={H - 8}>
        {t("surveys.plots.axis.east")} ({fmtDepth(b.maxE, locale)})
      </Label>
      <Label x={12} y={H / 2} rotate={-90}>
        {t("surveys.plots.axis.north")} ({fmtDepth(b.maxN, locale)})
      </Label>
    </PlotFrame>
  );
}

/** Vertical section: Vsection (x) vs TVD (y, increasing downward). */
function VSectionView({ pts }: { pts: ComputedStation[] }) {
  const { t, locale } = useLocale();
  const b = computeBounds(pts)!;
  const sx = scaler(Math.min(0, b.minVs), b.maxVs || 1, PAD, W - PAD);
  // TVD increases downward: min TVD at top, max at bottom.
  const sy = scaler(b.minTvd, b.maxTvd || 1, PAD, H - PAD);
  const scr = pts.map((p) => ({ x: sx(p.vsection), y: sy(p.tvd) }));
  return (
    <PlotFrame title={t("surveys.plots.vsection")}>
      <line x1={PAD} y1={PAD} x2={W - PAD} y2={PAD} stroke="var(--line)" />
      <line x1={PAD} y1={PAD} x2={PAD} y2={H - PAD} stroke="var(--line)" />
      <Path pts={scr} id="vs" />
      <Label x={W / 2} y={H - 8}>
        {t("surveys.plots.axis.vs")} ({fmtDepth(b.maxVs, locale)})
      </Label>
      <Label x={12} y={H / 2} rotate={-90}>
        {t("surveys.plots.axis.tvd")} ({fmtDepth(b.maxTvd, locale)})
      </Label>
    </PlotFrame>
  );
}

/** Placeholder shown in the 3D slot while the three.js chunk loads. */
function ThreeDLoading() {
  const { t } = useLocale();
  return (
    <div style={{ flex: "1 1 0", minWidth: 0 }}>
      <div className="subtitle" style={{ margin: "0 0 6px", fontWeight: 600, color: "var(--dim)" }}>
        {t("surveys.plots.threeD")}
      </div>
      <div
        style={{
          width: "100%",
          height: 300,
          minHeight: 240,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "var(--surface)",
          border: "1px solid var(--line)",
          borderRadius: 8,
          color: "var(--dim)",
          fontSize: 12,
        }}
      >
        {t("surveys.plots.threeD.loading")}
      </div>
    </div>
  );
}

/**
 * SurveyPlots — computes the run once and renders the three views side by side.
 * Falls back to a dim note when a run has no plottable (inc/azi-bearing) rows.
 */
export function SurveyPlots({ stations }: { stations: SurveyStationOut[] }) {
  const { t } = useLocale();
  const pts = computeMinCurvature(
    (Array.isArray(stations) ? stations : []).map((s) => ({
      md: s.md,
      inclination: s.inclination,
      azimuth: s.azimuth,
    })),
  );

  if (pts.length < 2) {
    return (
      <p className="subtitle" style={{ color: "var(--dim)", marginTop: 10 }}>
        {t("surveys.plots.empty")}
      </p>
    );
  }

  return (
    <div style={{ marginTop: 12 }}>
      <div className="subtitle" style={{ fontWeight: 600, marginBottom: 8, color: "var(--ink)" }}>
        {t("surveys.plots.title")}
      </div>
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        <PlanView pts={pts} />
        <VSectionView pts={pts} />
        <Suspense fallback={<ThreeDLoading />}>
          <Trajectory3D pts={pts} />
        </Suspense>
      </div>
    </div>
  );
}
